import { SuggestionCell, toCellDisplay } from '@ayocore/platform/client/components/EditableGrid';
import type {
  EditableGridColumn,
  EditableGridSuggestOption,
} from '@ayocore/platform/client/components/EditableGrid';
import { productRepository } from '@ayocore/catalog/client/rpc';
import type { Product } from '@ayocore/catalog/schemas/product.schema';
import type { ProductColumnKey, ProductPatch, ProductSource } from './types';

type ProductSuggestionColumnOptions = {
  title?: string;
  width?: number;
  placeholder?: string;
  /** Значение product_name, которое визуально равноценно пустоте в редакторе (см. EditableGridColumn.placeholderValue). */
  placeholderValue?: string;
};

function getProductDisplayName(row: ProductSource): string {
  return row.product_name ?? row.product?.name ?? '';
}

function mapProductsToSuggestOptions(rows: Product[]): EditableGridSuggestOption[] {
  return (rows ?? [])
    .filter(row => row.id != null)
    .map(row => ({
      id: String(row.id),
      label: row.name ?? `ID ${row.id}`,
      hint: row.sku ?? undefined,
      payload: row,
    }));
}

async function searchProducts(query: string): Promise<EditableGridSuggestOption[]> {
  const rows = (await productRepository
    .search(query)
    .filterByActive()
    .limit(8)
    .fetch()) as Product[];
  return mapProductsToSuggestOptions(rows ?? []);
}

/**
 * Фабрика колонки подсказки по товарам для EditableGrid.
 * Подходит для строк с product_id, product_name и опционально product (связь).
 *
 * @example
 * const columns = [
 *   createProductSuggestionColumn(),
 *   NumberCell({ key: 'qty', title: 'Кол-во', width: 80 }),
 * ];
 */
export function createProductSuggestionColumn<Row extends ProductSource>(
  options: ProductSuggestionColumnOptions = {}
): EditableGridColumn<Row, ProductPatch, ProductColumnKey> {
  return SuggestionCell<ProductSource, ProductPatch, ProductColumnKey>({
    key: 'product',
    title: options.title ?? 'Товар',
    width: options.width ?? 260,
    placeholder: options.placeholder ?? 'Поиск по названию или артикулу...',
    placeholderValue: options.placeholderValue,
    minChars: 2,
    getDisplayFromRow: row =>
      toCellDisplay(getProductDisplayName(row), { linked: row.product_id != null }),
    getEditorFromRow: row => ({
      value: getProductDisplayName(row),
      meta: { selectedRef: row.product_id ?? null },
    }),
    getSelectedRefFromRow: row => row.product_id ?? null,
    onSearch: searchProducts,
    onPatch: ({ row, value, selectedRef }) => {
      const typedName = value.trim();
      const nextProductId = typeof selectedRef === 'number' ? selectedRef : null;
      const prevProductId = row.product_id ?? null;
      const prevProductName = row.product_name ?? null;

      // В API не отправляем product_name: null — схемы вроде deal_row требуют строку (minLength: 1).
      // Отправляем введённое имя, при выборе из подсказки — label опции; при пустом — fallback.
      const apiProductName =
        typedName || (nextProductId != null ? 'Товар из каталога' : 'Новая позиция');

      const nextProductName = nextProductId != null ? null : typedName || null;

      return {
        hasChanges:
          nextProductId !== prevProductId ||
          apiProductName !== (prevProductName ?? 'Новая позиция'),
        apiPatch: {
          product_id: nextProductId,
          product_name: apiProductName,
        },
        localPatch: {
          product_id: nextProductId,
          product_name: nextProductId != null ? typedName || null : nextProductName,
          product: null,
        },
      };
    },
  }) as EditableGridColumn<Row, ProductPatch, ProductColumnKey>;
}
